import React, { useState } from "react";
import { gql, useMutation } from "@apollo/client";
import { Lock, Loader, Eye, EyeOff, Check } from "lucide-react";
import { validatePassword, PASSWORD_REQUIREMENTS_MESSAGE } from "@/utils/passwordValidation";

type ChangePasswordProps = {
  embedded?: boolean;
  onComplete?: () => void | Promise<void>;
};

const CHANGE_PASSWORD = gql`
  mutation ChangePassword($password: String!) {
    changePassword(password: $password) {
      message
      responseCode
      responseID
      extra
    }
  }
`;

export const ChangePassword = ({ embedded = false, onComplete }: ChangePasswordProps = {}) => {
  const [state, setState] = useState({
    newPassword: "",
    confirmPassword: "",
    loading: false,
    message: "",
    success: false,
    showPassword: false,
    showConfirmPassword: false
  });

  const [changePassword] = useMutation(CHANGE_PASSWORD);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!state.newPassword || !state.confirmPassword) {
      setState({ ...state, message: "Please fill in all fields", success: false });
      return;
    }

    if (state.newPassword !== state.confirmPassword) {
      setState({ ...state, message: "Passwords do not match", success: false });
      return;
    }

    const validation = validatePassword(state.newPassword);
    if (!validation.valid) {
      setState({ 
        ...state, 
        message: validation.errors.join(". "), 
        success: false 
      });
      return;
    }

    setState({ ...state, loading: true, message: "" });
    
    try {
      const result = await changePassword({
        variables: {
          password: state.newPassword,
        },
      });
      
      if (result.data?.changePassword) {
        const response = result.data.changePassword;
        const success = response.responseCode === 200;
        setState({
          ...state,
          loading: false,
          message: response.message || "Password changed successfully",
          success: success,
          newPassword: success ? "" : state.newPassword,
          confirmPassword: success ? "" : state.confirmPassword
        });
        if (success && embedded && typeof onComplete === 'function') {
          await onComplete();
        }
      }
    } catch (error: any) {
      setState({
        ...state,
        loading: false,
        message: error.message || "An error occurred. Please try again.",
        success: false
      });
    }
  };

  const getPasswordStrengthColor = (password: string) => {
    const validation = validatePassword(password);
    if (!password) return "border-gray-300";
    return validation.valid ? "border-green-500" : "border-red-300";
  };

  return (
    <div className={embedded ? "w-full p-0" : "max-w-2xl mx-auto p-6"}>
      <div className={embedded ? "bg-white p-8" : "bg-white rounded-lg shadow-md p-8"}>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Change Password
          </h1>
          <p className="text-gray-600">
            Update your account password. Make sure it's secure and memorable.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* New Password Field */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              New Password
            </label>
            <div className={`flex items-center border rounded-md ${getPasswordStrengthColor(state.newPassword)}`}>
              <Lock className="h-5 w-5 text-gray-400 mx-3" />
              <input
                type={state.showPassword ? "text" : "password"}
                value={state.newPassword}
                onChange={(e) => setState({ ...state, newPassword: e.target.value })}
                className="w-full py-3 border-0 focus:ring-0 text-base"
                style={{ margin: 0, padding: "12px 0 12px 10px" }}
                placeholder="Enter new password"
                disabled={state.loading}
              />
              <button
                type="button"
                aria-label={state.showPassword ? "Hide new password" : "Show new password"}
                onClick={() => setState({ ...state, showPassword: !state.showPassword })}
                className="mx-3 text-gray-400 hover:text-gray-600"
              >
                {state.showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            <div className="text-sm text-gray-500">
              {PASSWORD_REQUIREMENTS_MESSAGE}
            </div>
          </div>

          {/* Confirm Password Field */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Confirm New Password
            </label>
            <div className={`flex items-center border rounded-md ${
              state.confirmPassword && state.newPassword === state.confirmPassword 
                ? "border-green-500" 
                : state.confirmPassword 
                  ? "border-red-300" 
                  : "border-gray-300"
            }`}>
              <Lock className="h-5 w-5 text-gray-400 mx-3" />
              <input
                type={state.showConfirmPassword ? "text" : "password"}
                value={state.confirmPassword}
                onChange={(e) => setState({ ...state, confirmPassword: e.target.value })}
                className="w-full py-3 border-0 focus:ring-0 text-base"
                style={{ margin: 0, padding: "12px 0 12px 10px" }}
                placeholder="Confirm new password"
                disabled={state.loading}
              />
              <button
                type="button"
                aria-label={state.showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                onClick={() => setState({ ...state, showConfirmPassword: !state.showConfirmPassword })}
                className="mx-3 text-gray-400 hover:text-gray-600"
              >
                {state.showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {state.message && (
            <div className={`text-sm p-4 rounded-md flex items-center ${
              state.success 
                ? "bg-green-50 text-green-800 border border-green-200" 
                : "bg-red-50 text-red-800 border border-red-200"
            }`}>
              {state.success && <Check className="h-4 w-4 mr-2" />}
              {state.message}
            </div>
          )}

          <div className={`flex items-center pt-4 ${embedded ? 'justify-end' : 'justify-between'}`}>
            {!embedded && (
              <button
                type="button"
                onClick={() => window.history.back()}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Cancel
              </button>
            )}
            
            <button
              type="submit"
              disabled={state.loading}
              className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {state.loading ? (
                <>
                  <Loader className="h-4 w-4 animate-spin mr-2" />
                  Changing Password...
                </>
              ) : (
                "Change Password"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
